import { createFileRoute, Link } from "@tanstack/react-router";
import { ShieldCheck, Scale, Lock, AlertTriangle, BookOpen, FileText } from "lucide-react";
import { site } from "@/lib/site";

export const Route = createFileRoute("/disclaimer")({
  head: () => ({
    meta: [
      { title: "Legal Disclaimer, Scope of Services & POPIA Notice | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "Important legal notice: Nkadimeng Danny is not an admitted attorney. Full disclaimer, scope of services, terms of use and POPIA privacy notice.",
      },
      { property: "og:title", content: "Legal Disclaimer & POPIA Notice" },
      {
        property: "og:description",
        content:
          "Read our full disclaimer, scope of services and privacy notice.",
      },
    ],
  }),
  component: DisclaimerPage,
});

function DisclaimerPage() {
  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Legal disclaimer &amp; notices
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            Please read this notice carefully before engaging our services or
            acting on any information on this website.
          </p>
        </div>
      </section>

      <section className="container-narrow grid gap-10 py-16 md:grid-cols-[1fr_2.4fr]">
        <aside className="h-fit space-y-1 rounded-xl border border-border bg-card p-5 text-sm shadow-soft md:sticky md:top-24">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-gold">
            On this page
          </p>
          {[
            ["#status", "Status of practitioner"],
            ["#scope", "Scope of services"],
            ["#no-advice", "Not legal advice"],
            ["#representation", "Court representation"],
            ["#fidelity", "Trust account & funds"],
            ["#information", "Website information"],
            ["#popia", "POPIA / privacy notice"],
            ["#terms", "Terms of use"],
            ["#complaints", "Complaints"],
          ].map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="block rounded-md px-2 py-1.5 text-muted-foreground hover:bg-secondary hover:text-navy"
            >
              {label}
            </a>
          ))}
        </aside>

        <article className="space-y-12">
          <div className="rounded-xl border-l-4 border-gold bg-gold/10 p-5">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-gold" />
              <p className="text-sm leading-relaxed">
                <strong>Nkadimeng Danny is an LLB graduate (UNISA) and is
                NOT an admitted attorney, advocate or conveyancer.</strong>{" "}
                The practice operates as a legal support, paralegal and
                compliance services business. We do provide legal information,
                legal advice on non-reserved matters and legal opinions. We do
                not advise on or undertake matters reserved by the Legal
                Practice Act 28 of 2014, we do not represent clients in formal
                court proceedings, and we do not hold a Legal Practitioners'
                Fidelity Fund Certificate.
              </p>

            </div>
          </div>

          <Section id="status" icon={Scale} title="1. Status of the practitioner">
            <p>
              Nkadimeng Danny holds an LLB degree from the University of South
              Africa (UNISA). The founder has <strong>not been admitted and
              enrolled</strong> as a legal practitioner of the High Court of
              South Africa in terms of the Legal Practice Act 28 of 2014, and
              is not regulated by the Legal Practice Council (LPC) as an
              admitted attorney or advocate. Where the LPA or LPC rules
              require an admitted practitioner, we refer the matter to one.
            </p>
          </Section>

          <Section id="scope" icon={ShieldCheck} title="2. Scope of services">
            <p>The practice provides:</p>
            <ul>
              <li>Paralegal and legal-support services</li>
              <li>Labour relations support (internal hearings, CCMA preparation, document support)</li>
              <li>Legal drafting (contracts, policies, letters, affidavits — for commissioning elsewhere)</li>
              <li>Compliance and POPIA support services for SMEs</li>
              <li>Debt negotiation and consumer-rights guidance</li>
              <li>Court Online / CaseLines training and filing support</li>
            </ul>
            <p>
              We do <strong>not</strong> provide: legal opinions reserved for
              admitted practitioners, formal court appearances, the
              registration of property transfers, drafting of wills for a fee
              where reserved, or any service reserved by law to admitted
              attorneys or advocates.
            </p>
          </Section>

          <Section id="no-advice" icon={BookOpen} title="3. Not legal advice">
            <p>
              Nothing on this website, in our publications, in our newsletter,
              in the AI Legal Assistant chat, or in any consultation
              constitutes <strong>legal advice</strong>. All information is
              general in nature, may not reflect the most current legal
              developments, and is not a substitute for advice from an
              admitted legal practitioner on your specific facts. You should
              not act, or refrain from acting, on the basis of any information
              provided without seeking professional advice tailored to your
              circumstances.
            </p>
          </Section>

          <Section id="representation" icon={Scale} title="4. No representation">
            <p>
              We do <strong>not</strong> represent clients in any forum. This
              includes the High Court, Magistrates' Court, Constitutional
              Court, the CCMA, bargaining councils, statutory tribunals and
              internal disciplinary hearings. Our role is strictly
              behind-the-scenes legal support: case preparation, drafting,
              bundles, witness preparation, procedural guidance and
              coaching. Where appearance or representation is required, we
              refer the matter to an admitted attorney or a duly authorised
              representative.
            </p>
          </Section>

          <Section id="fidelity" icon={Lock} title="5. Trust account & client funds">
            <p>
              We do <strong>not</strong> operate a legal practitioner's trust
              account and we do <strong>not</strong> hold a Fidelity Fund
              Certificate. We will never receive money on behalf of a client
              that should properly be held in a trust account. Fees for our
              services are paid directly to our business account and are
              invoiced transparently.
            </p>
          </Section>

          <Section id="information" icon={FileText} title="6. Website information & accuracy">
            <p>
              We take reasonable care to keep information on this website
              accurate and up to date, but we make no warranty as to its
              completeness, accuracy or suitability for any particular
              purpose. South African law changes, and information on this
              site may become out of date. We accept no liability for any
              loss arising from reliance on website content.
            </p>
          </Section>

          <Section id="popia" icon={Lock} title="7. POPIA & privacy notice">
            <p>
              We process personal information in accordance with the
              Protection of Personal Information Act 4 of 2013 (POPIA).
            </p>
            <ul>
              <li>
                <strong>What we collect:</strong> name, contact details,
                consultation details and any information you choose to share
                about your matter.
              </li>
              <li>
                <strong>Why:</strong> to respond to your enquiry, prepare
                for and deliver the consultation or service, and (if you
                opt in) to send you newsletters or updates.
              </li>
              <li>
                <strong>Who we share with:</strong> nobody, unless required
                by law, with your consent, or with a referral practitioner
                you have asked us to engage.
              </li>
              <li>
                <strong>How long:</strong> we keep records for as long as
                required by law and for legitimate business purposes.
              </li>
              <li>
                <strong>Your rights:</strong> you may request access,
                correction or deletion of your personal information by
                emailing <a href={`mailto:${site.email}`}>{site.email}</a>.
              </li>
            </ul>
          </Section>

          <Section id="terms" icon={FileText} title="8. Terms of use">
            <p>
              By using this website you agree that (a) you will not use any
              content as a substitute for professional advice, (b) no
              attorney-client relationship is created by your use of this
              site or by submitting an enquiry, and (c) any engagement is
              subject to a written letter of engagement setting out the
              scope, fees and limits of our service.
            </p>
          </Section>

          <Section id="complaints" icon={AlertTriangle} title="9. Complaints">
            <p>
              We take complaints seriously. If you are unhappy with any
              aspect of our service, please contact us at{" "}
              <a href={`mailto:${site.email}`}>{site.email}</a> so we can
              address the matter promptly and in writing.
            </p>
          </Section>

          <div className="rounded-xl bg-navy p-6 text-navy-foreground">
            <p className="text-sm">
              By engaging Nkadimeng Danny Legal and Compliance Support, you
              confirm that you have read and understood this notice.
            </p>
            <Link
              to="/contact"
              className="mt-4 inline-flex items-center justify-center rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-gold-foreground"
            >
              Contact us with questions
            </Link>
          </div>
        </article>
      </section>
    </>
  );
}

function Section({
  id,
  title,
  icon: Icon,
  children,
}: {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-28">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold">
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="font-display text-2xl text-navy">{title}</h2>
      </div>
      <div className="prose prose-sm mt-4 max-w-none text-muted-foreground prose-strong:text-foreground prose-a:text-navy prose-a:underline prose-ul:my-2 prose-li:my-1">
        {children}
      </div>
    </section>
  );
}
